import api from '../api';
import type { 
    SubscriptionPlan, 
    SubscriptionSettings,
    SubscriptionSettingsResponse,
    SubscriptionSettingsWithTotal,
    ReconciliationResult,
    ReconciliationItem,
    ReconciliationRequest
} from '../types/subscription';

interface CheckoutResponse {
    sessionId: string;
    url: string;
}

export const SubscriptionService = {
    async getPlans(token: string): Promise<SubscriptionPlan[]> {
        const response = await api.get('/subscription/plans', {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    },

    async createCheckoutSession(token: string, successUrl?: string, planId?: number): Promise<CheckoutResponse> {
        const defaultSuccessUrl = `${process.env.REACT_APP_FRONTEND_URL}/subscription/success`;
        const defaultCancelUrl = `${process.env.REACT_APP_FRONTEND_URL}/subscription/cancel`;

        const response = await api.post<CheckoutResponse>('/subscription/checkout', {
            success_url: `${successUrl || defaultSuccessUrl}?session_id={CHECKOUT_SESSION_ID}`,
            cancel_url: defaultCancelUrl,
            plan_id: planId
        }, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        });
        
        if (!response.data.sessionId || !response.data.url) {
            throw new Error('Invalid checkout session response');
        }
        
        return response.data;
    },

    async verifySession(sessionId: string, token: string, retries = 3, delay = 2000): Promise<boolean> {
        try {
            console.log(`Verifying subscription session: ${sessionId}, attempt ${4-retries} of 3`);
            const response = await api.get(`/subscription/verify/${sessionId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            console.log('Subscription verification successful');
            return response.data.status === 'success';
        } catch (error) {
            console.error('Failed to verify subscription session:', error);
            
            // If we have retries left, wait and try again
            if (retries > 1) {
                console.log(`Retrying subscription verification in ${delay/1000} seconds...`);
                await new Promise(resolve => setTimeout(resolve, delay));
                return this.verifySession(sessionId, token, retries - 1, delay);
            }
            
            return false;
        }
    },

    async getSettings(token: string): Promise<SubscriptionSettingsWithTotal> {
        const response = await api.get('/subscription/settings', {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    },

    async updateSettings(settings: SubscriptionSettings, token: string): Promise<SubscriptionSettingsResponse> {
        const response = await api.post('/subscription/settings', settings, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    },

    // Start a new reconciliation job
    async startReconciliationJob(token: string): Promise<{job_id: string, status: string, message: string}> {
        try {
            const response = await api.get('/subscription/reconcile/check', {
                headers: { Authorization: `Bearer ${token}` }
            });
            return response.data;
        } catch (error: any) {
            console.error('Error starting reconciliation job:', error);
            throw error;
        }
    },
    
    // Check the status of a reconciliation job
    async checkReconciliationJobStatus(token: string, jobId: string, retries: number = 3): Promise<ReconciliationResult> {
        try {
            const response = await api.get('/subscription/reconcile/check', {
                headers: { Authorization: `Bearer ${token}` },
                params: { job_id: jobId }
            });
            return response.data;
        } catch (error: any) {
            // Implement retry logic with exponential backoff for 502 errors
            if (retries > 0 && error?.response && (error.response?.status === 502 || error.response?.status === 504)) {
                console.log(`Retrying job status check after 502/504 error. Retries left: ${retries}`);
                // Wait with exponential backoff (1s, 2s, 4s)
                await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, 3-retries)));
                return this.checkReconciliationJobStatus(token, jobId, retries - 1);
            }
            throw error;
        }
    },

    async reconcileSubscription(request: ReconciliationRequest, token: string): Promise<ReconciliationResult> {
        // This reconciles a specific subscription
        const response = await api.post('/subscription/reconcile/item', request, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    },

    async reconcileAllSubscriptions(token: string): Promise<ReconciliationResult> {
        // This reconciles all subscriptions at once
        const response = await api.post('/subscription/reconcile', {}, {
            headers: { Authorization: `Bearer ${token}` }
        });
        return response.data;
    }
};
